'use client'

/**
 * <AwardGallery /> — R102.6 (Stephan-Wunsch 2026-05-31 nachts)
 *
 * Klickbare Premium-Galerie für die Wettbewerbsordnung. Award-würdiges Design:
 * Gold-Akzent-Hover, smooth scale-in, Glassmorphism-Lightbox mit Backdrop-Blur,
 * Keyboard-Navigation (← / → / Esc), Bild-Counter, Cubic-Bezier-Transitions.
 *
 * Eingesetzt in § 8 (Terrain-Referenzen) und § 9 (Drei-Finger-Regel).
 */

import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react'

export interface AwardGalleryItem {
  src: string
  alt: string
  caption: string
}

export interface AwardGalleryProps {
  items: AwardGalleryItem[]
  /** Spalten-Mindestbreite — passt sich responsiv an. Default 280px. */
  minColWidth?: number
  /** Aspect-Ratio der Thumbnails. Default '16/9'. */
  aspect?: string
}

export function AwardGallery({ items, minColWidth = 280, aspect = '16/9' }: AwardGalleryProps) {
  const [activeIdx, setActiveIdx] = useState<number | null>(null)

  const close = useCallback(() => setActiveIdx(null), [])
  const next = useCallback(() => {
    setActiveIdx((i) => (i === null ? null : (i + 1) % items.length))
  }, [items.length])
  const prev = useCallback(() => {
    setActiveIdx((i) => (i === null ? null : (i - 1 + items.length) % items.length))
  }, [items.length])

  // Keyboard-Navigation
  useEffect(() => {
    if (activeIdx === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      else if (e.key === 'ArrowRight') next()
      else if (e.key === 'ArrowLeft') prev()
    }
    window.addEventListener('keydown', onKey)
    // Scroll-Lock während Lightbox offen
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [activeIdx, close, next, prev])

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(${minColWidth}px, 1fr))`,
          gap: '0.85em',
        }}
      >
        {items.map((item, idx) => (
          <motion.button
            key={item.src}
            type="button"
            onClick={() => setActiveIdx(idx)}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5, delay: idx * 0.06, ease: [0.22, 1, 0.36, 1] }}
            className="award-gallery-card"
            style={{
              all: 'unset',
              cursor: 'zoom-in',
              display: 'block',
              borderRadius: '14px',
              overflow: 'hidden',
              background: 'var(--award-ink-2)',
              border: '1px solid var(--award-line)',
              position: 'relative',
              transition: 'transform 320ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 320ms cubic-bezier(0.22, 1, 0.36, 1), border-color 320ms',
            }}
          >
            <div
              style={{
                position: 'relative',
                width: '100%',
                aspectRatio: aspect,
                overflow: 'hidden',
              }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={item.src}
                alt={item.alt}
                loading="lazy"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 800ms cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              />
              {/* Subtle gradient + Zoom-Icon Overlay */}
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.45) 100%)',
                  opacity: 0.85,
                  pointerEvents: 'none',
                }}
              />
              <span
                aria-hidden
                className="award-gallery-zoom"
                style={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  height: 32,
                  width: 32,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  background: 'color-mix(in oklch, var(--award-gold) 18%, rgba(0,0,0,0.55))',
                  border: '1px solid color-mix(in oklch, var(--award-gold) 45%, transparent)',
                  color: 'var(--award-gold)',
                  backdropFilter: 'blur(8px)',
                  opacity: 0,
                  transform: 'scale(0.88)',
                  transition: 'opacity 240ms ease, transform 240ms ease',
                }}
              >
                <ZoomIn size={16} />
              </span>
            </div>
            <div
              style={{
                padding: '0.55em 0.85em',
                borderTop: '1px solid var(--award-line)',
                background: 'color-mix(in oklch, var(--award-ink-2) 92%, var(--award-gold) 0%)',
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: '0.85em',
                  lineHeight: 1.35,
                  color: 'var(--award-cirrus-2)',
                  letterSpacing: '0.01em',
                }}
              >
                {item.caption}
              </p>
            </div>
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {activeIdx !== null && (
          <motion.div
            key="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28 }}
            onClick={close}
            role="dialog"
            aria-modal="true"
            aria-label="Bilder-Galerie"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 80,
              background: 'rgba(2, 6, 12, 0.78)',
              backdropFilter: 'blur(18px) saturate(120%)',
              WebkitBackdropFilter: 'blur(18px) saturate(120%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'clamp(12px, 3vw, 32px)',
            }}
          >
            {/* Close-Button */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); close() }}
              aria-label="Galerie schließen"
              style={{
                position: 'fixed',
                top: 'clamp(14px, 2.4vw, 28px)',
                right: 'clamp(14px, 2.4vw, 28px)',
                width: 44,
                height: 44,
                borderRadius: '50%',
                border: '1px solid color-mix(in oklch, var(--award-gold) 35%, transparent)',
                background: 'color-mix(in oklch, var(--award-ink) 70%, transparent)',
                color: 'var(--award-cirrus)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(8px)',
                transition: 'background 200ms, transform 200ms',
                zIndex: 2,
              }}
            >
              <X size={20} />
            </button>

            {/* Prev */}
            {items.length > 1 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prev() }}
                aria-label="Vorheriges Bild"
                style={{
                  position: 'fixed',
                  left: 'clamp(10px, 2vw, 28px)',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  border: '1px solid color-mix(in oklch, var(--award-gold) 35%, transparent)',
                  background: 'color-mix(in oklch, var(--award-ink) 70%, transparent)',
                  color: 'var(--award-cirrus)',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(8px)',
                  zIndex: 2,
                }}
              >
                <ChevronLeft size={24} />
              </button>
            )}

            {/* Next */}
            {items.length > 1 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); next() }}
                aria-label="Nächstes Bild"
                style={{
                  position: 'fixed',
                  right: 'clamp(10px, 2vw, 28px)',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  border: '1px solid color-mix(in oklch, var(--award-gold) 35%, transparent)',
                  background: 'color-mix(in oklch, var(--award-ink) 70%, transparent)',
                  color: 'var(--award-cirrus)',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(8px)',
                  zIndex: 2,
                }}
              >
                <ChevronRight size={24} />
              </button>
            )}

            {/* Image-Frame */}
            <motion.figure
              key={items[activeIdx].src}
              initial={{ opacity: 0, scale: 0.94, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              style={{
                margin: 0,
                maxWidth: 'min(96vw, 1400px)',
                maxHeight: '88vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.85em',
              }}
            >
              <div
                style={{
                  borderRadius: '18px',
                  overflow: 'hidden',
                  border: '1px solid color-mix(in oklch, var(--award-gold) 24%, transparent)',
                  boxShadow: '0 32px 80px -20px rgba(0,0,0,0.7), 0 0 60px -12px color-mix(in oklch, var(--award-gold) 18%, transparent)',
                  background: 'var(--award-ink)',
                  maxHeight: 'calc(88vh - 80px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={items[activeIdx].src}
                  alt={items[activeIdx].alt}
                  style={{
                    display: 'block',
                    maxWidth: '100%',
                    maxHeight: 'calc(88vh - 80px)',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                  }}
                />
              </div>
              <figcaption
                style={{
                  textAlign: 'center',
                  color: 'var(--award-cirrus-2)',
                  fontSize: '0.92em',
                  letterSpacing: '0.01em',
                  maxWidth: '720px',
                  padding: '0 0.5em',
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    fontSize: '0.78em',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: 'var(--award-gold)',
                    marginRight: '0.7em',
                  }}
                >
                  {String(activeIdx + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}
                </span>
                {items[activeIdx].caption}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hover-Effekte (gold-Border + scale + Zoom-Icon reveal) */}
      <style jsx>{`
        :global(.award-gallery-card:hover),
        :global(.award-gallery-card:focus-visible) {
          transform: translateY(-2px);
          border-color: color-mix(in oklch, var(--award-gold) 55%, transparent) !important;
          box-shadow: 0 24px 50px -22px rgba(0,0,0,0.6),
                      0 0 0 1px color-mix(in oklch, var(--award-gold) 25%, transparent),
                      0 0 32px -8px color-mix(in oklch, var(--award-gold) 30%, transparent);
        }
        :global(.award-gallery-card:hover img),
        :global(.award-gallery-card:focus-visible img) {
          transform: scale(1.045);
        }
        :global(.award-gallery-card:hover .award-gallery-zoom),
        :global(.award-gallery-card:focus-visible .award-gallery-zoom) {
          opacity: 1 !important;
          transform: scale(1) !important;
        }
      `}</style>
    </>
  )
}
